import { createI18n } from '@/i18n/setup/i18nSetup';
import { createAppRouter, initGuards } from '@/router';
import '@/styles/styles-hg.scss';
import domus, {
  HgSupportedLanguage,
  SupportedPlatforms,
  type PluginOptions,
} from '@smg-real-estate/domus';
import type { VueHeadClient } from '@unhead/vue';
import { createHead, VueHeadMixin } from '@unhead/vue/client';
import { createSSRApp, type App } from 'vue';
import type { Router } from 'vue-router';
import { Store } from 'vuex';
import AppComponent from './AppHg.vue';
import { createStore } from './store/store';

import { initSrpMappings, TargetPlatform } from '@smg-real-estate/hg-search-libs';

// SSR requires a fresh app instance per request, therefore we export a function
// that creates a fresh app instance. If using Vuex, we'd also be creating a
// fresh store here.
export function createApp(): {
  app: App<Element>;
  head: VueHeadClient;
  router: Router;
  store: Store<any>;
} {
  const app = createSSRApp(AppComponent);
  const store = createStore();
  const head = createHead();
  const i18n = createI18n();
  const router = createAppRouter(store);

  initGuards(router, i18n);
  initSrpMappings(TargetPlatform.HOMEGATE);

  app.use(head);
  app.mixin(VueHeadMixin);
  app.use(i18n);
  app.use(router);
  app.use(store);
  app.use(domus, {
    theme: SupportedPlatforms.hg,
    i18n: { instance: i18n, languages: { name: 'all', mode: 'lazy' } },
    router: { instance: router, defaultLanguage: HgSupportedLanguage.DE },
  } satisfies PluginOptions);

  return { app, head, router, store };
}
